import {
  ComponentService,
  DataTransmissionService,
  DbCommonColumnName,
  DevelopScriptServicePack,
  EditPanelElementModel,
  GocooEngineConfig,
  InputPartsListElementModel,
  InputPartsListItemModel,
  LabelAndValue,
  MessageService,
  ModelUtility,
  ResponseDataModel,
  ShowDefaultDialogService,
  ValidateCheckUtility,
  ViewType,
} from '@ktvs8/gocoo-engine-front-end';
import { MProgramSelectedResult } from '../develop/Dialog/m-program-dialog-condition-list';
import { ApplicationUserUtility, UserRole } from './application-user-utility';

/**
 * MProgramに関するユーティリティ
 *
 * @description
 * - 指定したメールアドレスが指定したMProgramのプロデューサーかどうかを判定する処理などを実装
 */
export namespace MProgramUtility {
  /**
   * 指定したメールアドレスが指定したMProgramのプロデューサーかどうかを判定する
   * @param mProgramTicketKey 対象の番組チケット
   * @param mailAddress メールアドレス
   * @returns
   */
  export async function isProducerOfProgram(mProgramTicketKey: string, mailAddress: string): Promise<boolean> {
    if (ValidateCheckUtility.isEmpty(mProgramTicketKey) || ValidateCheckUtility.isEmpty(mailAddress)) {
      return false;
    }

    try {
      // 指定したレコードを取得する
      const producerList = await fetchProducerEMailAddress(mProgramTicketKey);
      if (ValidateCheckUtility.isEmpty(producerList) || producerList.length === 0) {
        return true;
      }

      // プロデューサーのメールアドレスをチェック
      return producerList.includes(mailAddress);
    } catch (error) {
      return false;
    }
  }

  /**
   * 指定した番組のプロデューサーのメールアドレスを取得する
   * @param mProgramTicketKey
   * @returns
   */
  export async function fetchProducerEMailAddress(mProgramTicketKey: string): Promise<string[]> {
    if (ValidateCheckUtility.isEmpty(mProgramTicketKey)) {
      return [];
    }

    let componentService = new ComponentService(new ShowDefaultDialogService(), new MessageService());
    let dtService = new DataTransmissionService(componentService);

    // TicketKeyが一致するMProgramを取得する
    const condition = {
      [DbCommonColumnName.TICKET_TYPE]: 'MProgram',
      [DbCommonColumnName.TICKET_KEY]: mProgramTicketKey,
    };

    try {
      // 指定したレコードを取得する
      const result = await dtService.fetchRecord(ViewType.Edit, 'MProgram', condition, null, false, null);
      if (ValidateCheckUtility.isEmpty(result) || !(result as ResponseDataModel).isCompleted) {
        return [];
      }

      // 取得したメールアドレスを返却
      const producerList = ModelUtility.getValueInObjectAsArray(result.data, 'Producer');
      return producerList;
    } catch (error) {
      return [];
    }
  }

  /**
   * 現在ログインしているユーザーが指定したMProgramを編集できるかどうかを判定する
   * @param mProgramTicketKey
   */
  export async function canEditProgramByLoginUser(mProgramTicketKey: string): Promise<boolean> {
    if (ValidateCheckUtility.isEmpty(mProgramTicketKey)) {
      return false;
    }

    // 管理者の場合は編集可能
    const userRole = GocooEngineConfig.loginUser.userRole;
    if (userRole === 'ProductionManager') {
      return true;
    }

    // 法務・コンプライアンスは編集不可
    if (userRole === 'Legal') {
      return false;
    }

    // 自身のメールアドレスを基にチェック
    return await isProducerOfProgram(mProgramTicketKey, GocooEngineConfig.loginUser.mailAddress);
  }

  /**
   * 番組マスタのレコードを取得する
   * @param mProgramTicketKey
   * @returns
   */
  export async function fetchMProgramSelectedResult(mProgramTicketKey: string): Promise<MProgramSelectedResult> {
    const condition = {
      [DbCommonColumnName.TICKET_TYPE]: 'MProgram',
      [DbCommonColumnName.TICKET_KEY]: mProgramTicketKey,
    };

    let componentService = new ComponentService(new ShowDefaultDialogService(), new MessageService());
    let dtService = new DataTransmissionService(componentService);

    return new Promise<MProgramSelectedResult>((resolve, reject) => {
      const callBack = (result: ResponseDataModel) => {
        if (!result.isCompleted) {
          reject(result);
        }

        const model = <MProgramSelectedResult>{
          ticketKey: result.data['TicketKey'],
          programName: result.data['ProgramName'],
          isSpotProgram: result.data['IsSpotProgram'],
        };

        resolve(model);
      };

      dtService.fetchRecord(ViewType.Edit, 'MProgram', condition, callBack, false, null);
    });
  }

  /**
   * プロデューサーコンボボックスの選択肢リストを取得
   * @returns <LabelAndValue[]> プロデューサーコンボボックスの選択肢リスト
   */
  export async function fetchProducerSelectOptions(): Promise<LabelAndValue[]> {
    const producerList = await ApplicationUserUtility.fetchProducerList();
    let labelAndValueList: LabelAndValue[] = [];
    producerList.forEach((user: any) => {
      if (user.ApplicationRole === UserRole.Legal) {
        return;
      }
      labelAndValueList.push({ label: user.UserName, value: user.Email });
    });

    return labelAndValueList;
  }

  /**
   * 番組マスタのチケットーキーを待つ番組マスタレコードのプロデューサーリストを取得
   * @param mProgramTicketKey
   * @returns <string[]> プロデューサーのメールアドレスリスト
   */
  export async function fetchProducerListFilterMProgram(mProgramTicketKey: string): Promise<string[]> {
    const condition = {
      [DbCommonColumnName.TICKET_TYPE]: 'MProgram',
      [DbCommonColumnName.TICKET_KEY]: mProgramTicketKey,
    };

    let componentService = new ComponentService(new ShowDefaultDialogService(), new MessageService());
    let dtService = new DataTransmissionService(componentService);

    return new Promise<string[]>((resolve, reject) => {
      const callBack = (result: ResponseDataModel) => {
        if (!result.isCompleted) {
          reject(result);
        }

        resolve(result.data['Producer']);
      };

      dtService.fetchRecord(ViewType.Edit, 'MProgram', condition, callBack, false, null);
    });
  }

  /**
   * プロデューサーコンボボックスを設定する
   * @param editPanel
   * @param service
   * @returns
   */
  export function setupProducerSelectBox(
    editPanel: EditPanelElementModel,
    service: DevelopScriptServicePack,
    producerList: LabelAndValue[]
  ) {
    const producerColumn = editPanel.getInputParts('Producer');
    if (!producerColumn) {
      return;
    }
    const inputPartsList = producerColumn as InputPartsListElementModel;
    (inputPartsList.copyInputParts as InputPartsListItemModel).selectedList = producerList;

    inputPartsList.inputPartsList.forEach((inputParts) => {
      const select = inputParts as InputPartsListItemModel;
      if (select.selectedList.length > 0) {
        return;
      }
      select.selectedList = producerList;
    });
  }
}
